import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, LogOut, Handshake, Building2, Download, Search, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSEO } from "@/hooks/useSEO";
import { toast } from "sonner";

const CORP_STATUS = {
  da_contattare: { label: "Da contattare", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  in_trattativa: { label: "In trattativa", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  accordo_chiuso: { label: "Accordo chiuso", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export default function AdminDashboard() {
  useSEO({ title: "Dashboard — JetSeat", noindex: true });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [corporate, setCorporate] = useState([]);
  const [leadQ, setLeadQ] = useState("");
  const [corpQ, setCorpQ] = useState("");
  const [partnerQ, setPartnerQ] = useState("");
  const [corpStatusFilter, setCorpStatusFilter] = useState("all");

  const matches = (obj, q, keys) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return keys.some((k) => String(obj[k] ?? "").toLowerCase().includes(needle));
  };
  const filteredLeads = leads.filter((l) => matches(l, leadQ, ["company_name", "reference", "email", "mobile", "phone", "reason"]));
  const filteredCorp = corporate.filter((c) =>
    (corpStatusFilter === "all" || c.status === corpStatusFilter) &&
    matches(c, corpQ, ["business_name", "sector", "destinations", "contact_name", "business_email", "phone"]));
  const filteredPartners = partners.filter((p) => matches(p, partnerQ, ["company", "name", "email", "org_type", "phone"]));

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/admin/stats"), api.get("/leads"), api.get("/partnerships"), api.get("/corporate")])
      .then(([s, l, p, c]) => { setStats(s.data); setLeads(l.data); setPartners(p.data); setCorporate(c.data); })
      .catch(() => {});
  }, [user]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/corporate/${id}/status`, { status });
      setCorporate((list) => list.map((c) => (c.id === id ? { ...c, status } : c)));
      toast.success("Stato aggiornato");
    } catch (e) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const sendDigest = async () => {
    try {
      const { data } = await api.post("/admin/digest/send?force=true");
      toast.success(`Riepilogo inviato (${data.sent_items} richieste) a ${data.email}`);
    } catch (e) {
      toast.error("Invio riepilogo non riuscito");
    }
  };
  const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

  const downloadCsv = async (kind) => {
    try {
      const res = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jetseat_${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV avviato");
    } catch (e) {
      toast.error("Export non riuscito");
    }
  };

  const ExportButton = ({ kind }) => (
    <button
      onClick={() => downloadCsv(kind)}
      data-testid={`export-${kind}`}
      className="inline-flex items-center gap-2 rounded-full bg-pine px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-cream transition-transform duration-300 hover:scale-[1.04]"
    >
      <Download size={14} /> Esporta CSV
    </button>
  );

  const cards = [
    { icon: <Users size={18} />, label: "Lead totali", value: stats?.total_leads ?? "—" },
    { icon: <Building2 size={18} />, label: "Richieste aziendali", value: stats?.total_corporate ?? "—" },
    { icon: <Handshake size={18} />, label: "Partnership", value: stats?.total_partnerships ?? "—" },
  ];

  return (
    <div className="min-h-screen bg-secondary" data-testid="admin-dashboard">
      <header className="bg-midnight text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Logo dark />
          <div className="flex items-center gap-3 sm:gap-5">
            <span className="text-white/50 text-sm hidden sm:block">{user?.email}</span>
            <button data-testid="digest-btn" onClick={sendDigest}
              className="inline-flex items-center gap-2 rounded-full bg-pine px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-cream hover:scale-[1.04] transition-transform duration-300">
              <Mail size={14} /> Riepilogo ora
            </button>
            <button data-testid="logout-btn" onClick={doLogout}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-white/10 transition-colors duration-300">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Dashboard</h1>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-5">
          {cards.map((c) => (
            <div key={c.label} className="bg-card border border-border p-6" data-testid={`stat-${c.label}`}>
              <div className="text-accent">{c.icon}</div>
              <div className="mt-4 font-serif text-4xl">{c.value}</div>
              <div className="overline text-muted-foreground mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="leads" className="mt-12">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads">Lead ({leads.length})</TabsTrigger>
            <TabsTrigger value="corporate" data-testid="tab-corporate">Aziende ({corporate.length})</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">Partnership ({partners.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
              <div className="relative w-full sm:w-72">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={leadQ} onChange={(e) => setLeadQ(e.target.value)} data-testid="search-leads" placeholder="Cerca nome, email, azienda…" className="pl-9 h-10 bg-card border-border" />
              </div>
              <ExportButton kind="leads" />
            </div>
            <div className="bg-card border border-border overflow-x-auto">
              <Table data-testid="leads-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead><TableHead>Azienda</TableHead><TableHead>Riferimento</TableHead>
                    <TableHead>Contatto</TableHead><TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{fmt(l.created_at)}</TableCell>
                      <TableCell className="font-medium">{l.company_name}</TableCell>
                      <TableCell>{l.reference}</TableCell>
                      <TableCell>
                        <span className="block text-sm">{l.email}</span>
                        <span className="text-xs text-muted-foreground">{l.mobile}{l.phone ? ` · ${l.phone}` : ""}</span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs">{l.reason}</TableCell>
                    </TableRow>
                  ))}
                  {filteredLeads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Nessun lead.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="corporate" className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <div className="relative w-full sm:w-72">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={corpQ} onChange={(e) => setCorpQ(e.target.value)} data-testid="search-corporate" placeholder="Cerca azienda, settore, destinazioni…" className="pl-9 h-10 bg-card border-border" />
                </div>
                <Select value={corpStatusFilter} onValueChange={setCorpStatusFilter}>
                  <SelectTrigger data-testid="filter-corp-status" className="w-full sm:w-[180px] h-10 bg-card border-border"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    {Object.entries(CORP_STATUS).map(([k, v]) => (<SelectItem key={k} value={k}>{v.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <ExportButton kind="corporate" />
            </div>
            <div className="bg-card border border-border overflow-x-auto">
              <Table data-testid="corporate-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead><TableHead>Ragione sociale</TableHead><TableHead>Settore</TableHead>
                    <TableHead>Posti</TableHead><TableHead>Referente</TableHead><TableHead>Contatto</TableHead><TableHead>Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCorp.map((c) => (
                    <TableRow key={c.id} data-testid={`corp-row-${c.id}`}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{fmt(c.created_at)}</TableCell>
                      <TableCell className="font-medium">{c.business_name}{c.destinations ? <span className="block text-xs text-muted-foreground font-normal">→ {c.destinations}</span> : null}</TableCell>
                      <TableCell className="text-sm">{c.sector}</TableCell>
                      <TableCell><Badge variant="secondary">{c.estimated_seats}</Badge></TableCell>
                      <TableCell>{c.contact_name}<span className="block text-xs text-muted-foreground">{c.contact_role}</span></TableCell>
                      <TableCell><span className="block text-sm">{c.business_email}</span><span className="text-xs text-muted-foreground">{c.phone}</span></TableCell>
                      <TableCell>
                        <Select value={c.status} onValueChange={(v) => changeStatus(c.id, v)}>
                          <SelectTrigger data-testid={`corp-status-${c.id}`} className={`w-[150px] text-xs font-medium border ${CORP_STATUS[c.status]?.cls || ""}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(CORP_STATUS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredCorp.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Nessuna richiesta aziendale.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="partners" className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
              <div className="relative w-full sm:w-72">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={partnerQ} onChange={(e) => setPartnerQ(e.target.value)} data-testid="search-partners" placeholder="Cerca azienda, referente, tipo…" className="pl-9 h-10 bg-card border-border" />
              </div>
              <ExportButton kind="partnerships" />
            </div>
            <div className="bg-card border border-border overflow-x-auto">
              <Table data-testid="partners-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead><TableHead>Azienda</TableHead><TableHead>Referente</TableHead>
                    <TableHead>Contatto</TableHead><TableHead>Tipo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPartners.map((p) => (
                    <TableRow key={p.id} data-testid={`partner-row-${p.id}`}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{fmt(p.created_at)}</TableCell>
                      <TableCell className="font-medium">{p.company}</TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell><span className="block text-sm">{p.email}</span><span className="text-xs text-muted-foreground">{p.phone}</span></TableCell>
                      <TableCell><Badge variant="secondary">{p.org_type}</Badge></TableCell>
                    </TableRow>
                  ))}
                  {filteredPartners.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Nessuna partnership.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
